import { defineStore } from 'pinia'

export type Theme = 'light' | 'dark'
export type Locale = 'zh' | 'en'
export type AiTab = 'denoise' | 'pivot' | 'chain' | 'rebuttal'

export interface GeneratedReportSnapshot {
  title: string
  markdown: string
  charCount: number
  platform: string
  language: string
}

interface AppState {
  page: string                 // current route id, e.g. 'dashboard' | 's-workspace' | 'findings'
  theme: Theme
  locale: Locale
  sidebarCollapsed: boolean
  advancedMode: boolean
  isPro: boolean

  // Modals
  proModal: boolean
  devApiModal: boolean
  subscriptionModal: boolean
  sysStatusModal: boolean
  auditLogModal: boolean
  contactModal: boolean
  feedbackModal: boolean
  changelogModal: boolean
  reportPreviewModal: boolean
  reportPreviewPlatform: string
  generatedReport: GeneratedReportSnapshot | null
  triageVerdicts: Record<number, string>     // finding.id → user-set verdict (confirmed / fp / manual)
  triageReanalyzeBusy: boolean
  newScanModal: boolean
  newScanPrefillTargetId: number | null

  // Filters (per-page sub-nav)
  findingsFilter: string
  targetsFilter: string
  inventoryFilter: string
  monitorFilter: string
  reportsFilter: string

  // AI Triage tab
  aiTab: AiTab
}

export const useAppStore = defineStore('app', {
  state: (): AppState => ({
    page: 'dashboard',
    theme: 'dark',
    locale: 'zh',
    sidebarCollapsed: false,
    advancedMode: false,
    isPro: false,

    proModal: false,
    devApiModal: false,
    subscriptionModal: false,
    sysStatusModal: false,
    auditLogModal: false,
    contactModal: false,
    feedbackModal: false,
    changelogModal: false,
    reportPreviewModal: false,
    reportPreviewPlatform: 'HackerOne',
    generatedReport: null,
    triageVerdicts: {},
    triageReanalyzeBusy: false,
    newScanModal: false,
    newScanPrefillTargetId: null,

    findingsFilter: 'all',
    targetsFilter: 'all',
    inventoryFilter: 'all',
    monitorFilter: 'all',
    reportsFilter: 'all',

    aiTab: 'denoise',
  }),
  actions: {
    setTheme(t: Theme) {
      this.theme = t
      if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-theme', t)
      }
    },
    setLocale(l: Locale) {
      this.locale = l
      if (typeof document !== 'undefined') {
        document.documentElement.lang = l === 'zh' ? 'zh-CN' : 'en'
      }
    },
    setPage(p: string) {
      this.page = p
      if (typeof document !== 'undefined') {
        document.body.setAttribute('data-page', p)
      }
    },
    setGeneratedReport(snap: GeneratedReportSnapshot | null) {
      this.generatedReport = snap
    },
    setTriageVerdict(findingId: number, verdict: string) {
      this.triageVerdicts[findingId] = verdict
    },
    openNewScan(targetId: number | null = null) {
      this.newScanPrefillTargetId = targetId
      this.newScanModal = true
    },
    closeAllModals() {
      this.proModal = false
      this.devApiModal = false
      this.subscriptionModal = false
      this.sysStatusModal = false
      this.auditLogModal = false
      this.contactModal = false
      this.feedbackModal = false
      this.changelogModal = false
      this.reportPreviewModal = false
      this.newScanModal = false
    },
  },
  persist: {
    pick: ['theme', 'locale', 'sidebarCollapsed', 'advancedMode', 'isPro', 'triageVerdicts'],
  },
})
